/**
 * Oturum hatası sınıflandırması (BETRUN, 27.10.2026).
 *
 * Eskiden `/auth/me`'nin her hatası (ağ kopması, zaman aşımı, kesilen istek
 * (nginx 499), 500/503 "Sistem güncelleniyor") oturumu kapatıyordu: telefonda
 * banka uygulamasından dönen, oyundan çıkan ya da yayın sırasında sayfayı
 * yenileyen oyuncu çıkış yapmış oluyordu. Oturumu yalnız sunucunun **oturum**
 * 401'i bitirir; geri kalan her hata geçicidir ve jeton korunur.
 *
 * Sunucu oturum 401'lerine sebebi ``X-Auth-Error`` başlığıyla yazar
 * (backend/auth.py): ``missing`` (istek jetonsuz gitti), ``invalid`` (bozuk ya
 * da imzası tutmayan jeton), ``expired`` (süresi doldu), ``revoked`` (çıkış,
 * şifre/2FA değişikliği, hesap kapatıldı). Başlık yoksa (eski sunucu, spor
 * sağlayıcısı uçları) bilinen oturum metinlerine bakılır. İş kuralı 401'leri
 * (ör. "Kullanıcı adı veya şifre hatalı") oturum hatası **değildir**.
 */
export const TOKEN_KEY = 'pusubet_token';

export const SESSION_KINDS = ['missing', 'invalid', 'expired', 'revoked'];

// Sunucunun oturum 401 metinleri (backend/auth.py ve sağlayıcı açılış uçları).
const SESSION_DETAILS = {
  Yetkisiz: 'missing',
  'Geçersiz token': 'invalid',
  'Oturum süresi doldu': 'expired',
  'Oturum süresi doldu, tekrar giriş yapın': 'expired',
  'Oturum geçersiz': 'revoked',
};

export function readToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function writeToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Depolama kapalıysa (gizli mod kotası) oturum yalnız bu sayfada yaşar.
  }
}

/** İsteğin taşıdığı jeton (`Authorization: Bearer …`), yoksa ''. */
export function requestToken(config) {
  const headers = config?.headers;
  if (!headers) return '';
  const raw = typeof headers.get === 'function'
    ? headers.get('Authorization')
    : headers.Authorization || headers.authorization;
  const match = /^Bearer\s+(.+)$/.exec(String(raw || ''));
  return match ? match[1] : '';
}

function headerKind(response) {
  const headers = response?.headers;
  if (!headers) return '';
  const raw = typeof headers.get === 'function'
    ? headers.get('x-auth-error')
    : headers['x-auth-error'] || headers['X-Auth-Error'];
  const value = String(raw || '').trim().toLowerCase();
  return SESSION_KINDS.includes(value) ? value : '';
}

/**
 * Hatanın oturum sebebi: ``missing`` | ``invalid`` | ``expired`` | ``revoked``
 * ya da ``null`` (geçici hata ya da oturumla ilgisiz 401).
 *
 * ``sessionEndpoint``: yalnız oturumla korunan uç (`/auth/me`, `/auth/refresh`)
 * — sebebi okunamayan 401 burada oturum sonu sayılır (geçersiz jetonla erişim
 * hiçbir koşulda açık kalmaz). Öteki uçlarda okunamayan 401 oturumu kapatmaz.
 */
export function sessionFailure(error, { sessionEndpoint = false } = {}) {
  const response = error?.response;
  // Yanıt yok (ağ, zaman aşımı, iptal) ya da 401 dışı (499, 5xx, 429, 403): geçici.
  if (!response || response.status !== 401) return null;
  const fromHeader = headerKind(response);
  if (fromHeader) return fromHeader;
  // İstek jetonsuz gittiyse sebep jetonun yokluğudur (başka sekmede silindi).
  if (error.config && !requestToken(error.config)) return 'missing';
  const detail = typeof response.data?.detail === 'string' ? response.data.detail.trim() : '';
  if (SESSION_DETAILS[detail]) return SESSION_DETAILS[detail];
  return sessionEndpoint ? 'invalid' : null;
}

/** Jetonun sahibi (``sub``); imza doğrulanmaz, yalnız sekmeler arası karşılaştırma için. */
export function tokenSubject(token) {
  if (typeof token !== 'string') return '';
  const part = token.split('.')[1];
  if (!part) return '';
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return String(JSON.parse(atob(padded))?.sub || '');
  } catch {
    return '';
  }
}

/** Oturum hatası olayı: `lib/api.js` yayınlar, `AuthContext` dinler. */
export const AUTH_FAILURE_EVENT = 'bhs:auth-failure';
